// 주기 폴링의 "속도 조절기" — 지금 사람이 실제로 보고 있는지에 따라 조회 주기를 늘렸다 줄인다.
//
// 왜 만들었나 (2026-08-10 운영 로그 48시간 실측):
//   GET /api/v2/inbound-messages/alerts/pending 이 48시간에 23,950 건으로 헬스체크를 빼면 물량 1위였다
//   (브라우저 사전확인 OPTIONS 19,250 건이 따라붙어 실제 왕복은 거의 두 배). 한 건은 46ms 로 가볍지만
//   전부 인증 관문을 지나고, 그 관문은 태스크당 4칸뿐인 main DB 대기줄을 쓴다 — 그래서 "가벼운 조회"의
//   물량 자체가 장애 압력이 된다(8/8 22시·8/9 18시 5xx 스파이크의 느린 요청 1위가 이 경로, 5xx 77건).
//   건수를 역산하면 15초 주기 탭 2~4개가 매장 PC 에 하루 종일 열려 있는 모양이었다. 사람이 자리에 없는
//   시간까지 15초를 유지할 이유가 없다.
//
// 설계 원칙 — "확실히 안 보고 있을 때만 늦춘다":
//   폴링을 완전히 멈추지 않는다. 이 폴링은 화면 토스트만이 아니라 **탭이 숨어 있을 때의 데스크톱
//   알림**까지 먹여 살리므로, 멈추면 매장이 고객 문자를 놓친다(기능이 조용히 죽는다). 그래서 단계는
//   전부 "늦추기"이고, 사람이 돌아오는 순간(탭 복귀)에는 즉시 한 번 조회해 늦어진 몫을 메운다
//   (단 실제로 주기를 늦춘 만큼 숨어 있었을 때만 — 짧은 왕복까지 즉시조회하면 요청이 오히려 는다).
//
// 자리 비움 판정은 **탭이 보일 때만** 한다. 탭이 숨으면 우리 document 는 마우스·키 이벤트를 아예
// 못 받기 때문에, 숨은 탭에 자리비움 타이머를 적용하면 "다른 프로그램으로 열심히 일하는 중"을
// "자리 비움"으로 오판해 알림이 늦어진다.
//
// 숨은 직후 5분은 늦추지 않는다 (2026-08-10 리뷰 반영):
//   "숨은 탭은 어차피 브라우저가 분당 1회로 늦춘다"는 말은 **숨은 뒤 5분이 지난 다음**에만 맞다
//   (크롬 intensive wake-up throttling 이 5분 뒤에 걸린다). 그 전 5분은 15초 주기가 그대로 돌고
//   있었으므로, 숨는 즉시 60초로 내리면 "잠깐 다른 프로그램 보는 중"에 문자 인지가 15초 → 61초로
//   실제로 늦어진다. 그래서 숨김을 두 구간으로 쪼개 5분 이내는 원래 주기를 유지한다 — 물량 절감은
//   장시간 방치(hidden-throttled·hidden-long)에서 나오므로 이 구간을 살려도 절감 손실이 거의 없다.

import { useEffect, useRef, useState } from "react";

/** 지금 사람이 이 탭을 어떻게 대하고 있나 — 주기를 고르는 유일한 기준. */
export type PollPhase =
  /** 탭이 보이고 방금까지 조작했다 — 원래 주기(가장 빠름). */
  | "active"
  /** 탭은 보이는데 한동안 조작이 없다(자리 비움 추정). */
  | "idle"
  /** 방금 숨었다(잠깐 다른 창을 보는 중) — 브라우저도 아직 안 늦추므로 우리도 늦추지 않는다. */
  | "hidden"
  /** 숨은 채로 5분을 넘겼다 — 브라우저가 어차피 분당 1회로 정렬하는 구간. */
  | "hidden-throttled"
  /** 숨은 채로 아주 오래됐다(밤새 열어둔 탭) — 가장 느리게. */
  | "hidden-long";

export type PollPacingTiers = Record<PollPhase, number>;

export type PollPacingOptions = {
  /** false 면 조절기가 아무 일도 하지 않는다(리스너도 안 건다). */
  enabled?: boolean;
  /** 탭이 보이는데 이 시간만큼 조작이 없으면 idle 로 본다. */
  idleAfterMs?: number;
  /** 숨은 채로 이 시간을 넘기면 hidden-throttled 로 본다(브라우저가 스로틀을 시작하는 시점). */
  hiddenThrottleAfterMs?: number;
  /** 숨은 채로 이 시간을 넘기면 hidden-long 으로 본다. */
  hiddenLongAfterMs?: number;
  /**
   * 숨었다가 다시 보이게 된 순간 호출 — 여기서 즉시 한 번 조회해 공백을 메운다.
   * 단 아주 짧게 숨었다 돌아온 경우엔 부르지 않는다(아래 resumeMinHiddenMs).
   */
  onResume?: () => void;
  /**
   * 복귀 즉시조회의 최소 숨김 시간. 이보다 짧게 숨었다 돌아오면 즉시조회를 건너뛴다 —
   * 기본값은 tiers.active(=원래 주기)라, 건너뛰어도 그 주기가 곧 돌아 공백이 생기지 않는다.
   * 없으면 탭을 자주 왕복하는 사용자(폰보드↔통신사 포털)에게서 요청량이 오히려 늘어난다.
   */
  resumeMinHiddenMs?: number;
};

// 자리 비움 기준을 3분에서 10분으로 늘렸다(2026-08-10 리뷰 반영):
//   폰보드를 보조 모니터에 띄워 둔 채 주 모니터로 일하는 매장 구성에서는 탭이 계속 visible 이라
//   idle 로 내려가는데, 이때 hasFocus() 는 false 이므로 데스크톱 알림 경로가 살아 있다 = idle 단계는
//   화면 카드만이 아니라 **OS 알림까지** 늦춘다. 3분은 "잠깐 전화받는 중"에도 걸려 흔하고, 10분이면
//   실제 자리 비움에 가깝다. 하루 종일 켜둔 탭의 절감량은 이 문턱을 늘려도 거의 그대로다
//   (8시간 방치 중 앞 10분만 원래 주기).
const DEFAULT_IDLE_AFTER_MS = 10 * 60_000;
// 크롬이 숨은 페이지 타이머를 분당 1회로 정렬(intensive wake-up throttling)하기 시작하는 시점.
const DEFAULT_HIDDEN_THROTTLE_AFTER_MS = 5 * 60_000;
const DEFAULT_HIDDEN_LONG_AFTER_MS = 30 * 60_000;

// 단계 승격(active→idle, hidden→hidden-long)만 시계로 확인한다. 강등(숨김·조작 재개)은 이벤트로
// 즉시 처리하므로 이 시계가 느려도 사용자 체감에 영향이 없다 — 그래서 10초로 넉넉히 잡았다.
// (숨은 탭에서는 브라우저가 이 타이머도 분당 1회로 늦추는데, 그래도 상관없는 용도다.)
const PHASE_TICK_MS = 10_000;

/** 조작으로 볼 이벤트 — 아주 흔한 것만. mousemove 는 ref 갱신만 하므로 리렌더를 유발하지 않는다. */
const ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
  "mousemove",
] as const;

/**
 * 순수 판정 함수 — 시각 정보만으로 단계를 정한다(읽고 검토하기 쉽게 분리).
 * 이 파일 안에서만 쓴다(프론트에 테스트 러너가 없어 export 해도 부를 사람이 없다 — 러너가
 * 들어오면 이 함수만 export 해서 단계 경계를 표로 검증하면 된다).
 *
 * @param visible 탭이 보이는가
 * @param sinceActivityMs 마지막 조작으로부터 지난 시간(보일 때만 의미 있음)
 * @param sinceHiddenMs 숨겨진 뒤로 지난 시간(숨었을 때만 의미 있음)
 */
function resolvePollPhase(
  visible: boolean,
  sinceActivityMs: number,
  sinceHiddenMs: number,
  idleAfterMs: number,
  hiddenThrottleAfterMs: number,
  hiddenLongAfterMs: number,
): PollPhase {
  if (!visible) {
    if (sinceHiddenMs >= hiddenLongAfterMs) return "hidden-long";
    // 문턱이 뒤집혀 들어와도(설정 실수) 더 느린 단계로 미끄러지지 않게 min 으로 잘라둔다.
    if (sinceHiddenMs >= Math.min(hiddenThrottleAfterMs, hiddenLongAfterMs)) {
      return "hidden-throttled";
    }
    return "hidden";
  }
  return sinceActivityMs >= idleAfterMs ? "idle" : "active";
}

/**
 * 지금 써야 할 폴링 주기(ms)를 돌려준다. react-query 의 `refetchInterval` 에 그대로 꽂는다.
 *
 * 반환값이 바뀌면 react-query 가 타이머를 새로 잡으므로, 단계가 바뀐 시점부터 새 주기가 적용된다.
 */
export function usePollPacing(
  tiers: PollPacingTiers,
  options: PollPacingOptions = {},
): number {
  const {
    enabled = true,
    idleAfterMs = DEFAULT_IDLE_AFTER_MS,
    hiddenThrottleAfterMs = DEFAULT_HIDDEN_THROTTLE_AFTER_MS,
    hiddenLongAfterMs = DEFAULT_HIDDEN_LONG_AFTER_MS,
    onResume,
    resumeMinHiddenMs = tiers.active,
  } = options;

  const [phase, setPhase] = useState<PollPhase>("active");
  // 렌더 없이 현재 단계를 읽기 위한 사본 — mousemove 처리에서 setState 를 아예 안 부르려고 둔다.
  const phaseRef = useRef<PollPhase>("active");
  phaseRef.current = phase;
  const lastActivityRef = useRef(Date.now());
  const hiddenSinceRef = useRef<number | null>(null);
  // 콜백을 ref 로 들고 있어야 부모가 매번 새 함수를 넘겨도 리스너를 다시 걸지 않는다.
  const onResumeRef = useRef(onResume);
  onResumeRef.current = onResume;

  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;

    const recompute = () => {
      const now = Date.now();
      const visible = document.visibilityState === "visible";
      const next = resolvePollPhase(
        visible,
        now - lastActivityRef.current,
        hiddenSinceRef.current === null ? 0 : now - hiddenSinceRef.current,
        idleAfterMs,
        hiddenThrottleAfterMs,
        hiddenLongAfterMs,
      );
      if (phaseRef.current === next) return;
      phaseRef.current = next;
      setPhase(next);
    };

    const onActivity = () => {
      lastActivityRef.current = Date.now();
      // 이미 active 면 바뀔 게 없다 — mousemove 가 초당 수십 번 들어오므로 여기서 잘라야 한다.
      // (active→idle 승격은 아래 시계가 맡는다.)
      if (phaseRef.current === "active") return;
      recompute();
    };

    const onVisibility = () => {
      const visible = document.visibilityState === "visible";
      if (visible) {
        const now = Date.now();
        const hiddenForMs =
          hiddenSinceRef.current === null ? -1 : now - hiddenSinceRef.current;
        hiddenSinceRef.current = null;
        // 탭으로 돌아온 것 자체가 가장 확실한 "조작" 신호다.
        lastActivityRef.current = now;
        recompute();
        // 늦춰 두었던 동안 도착한 문자를 곧바로 받는다 — 이게 없으면 "알림이 늦게 온다"는 기능 저하다.
        //
        // 단 "충분히 오래 숨었을 때만" 부른다. visibilitychange 는 의도적 탭 전환뿐 아니라 창
        // 최소화·복원, 창 가림(occlusion) 판정 변화에도 발생하고, 매장 직원은 폰보드와 통신사
        // 포털을 수십 초 단위로 왕복한다 — 무조건 즉시조회하면 요청량이 원래보다 늘어나 이 조절기의
        // 목적과 정반대가 된다. 짧은 왕복은 그동안 주기를 늦춘 적도 없고(hidden 단계 = 원래 주기),
        // 곧 인터벌이 돌아오므로 건너뛰어도 잃는 것이 없다.
        if (hiddenForMs >= resumeMinHiddenMs) onResumeRef.current?.();
      } else {
        hiddenSinceRef.current = Date.now();
        recompute();
      }
    };

    // 초기 상태를 실제 탭 상태로 맞춘다(숨은 탭에서 마운트되는 경우가 있다 — 배경 탭 새로고침).
    if (document.visibilityState !== "visible") hiddenSinceRef.current = Date.now();
    recompute();

    document.addEventListener("visibilitychange", onVisibility);
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }
    const ticker = window.setInterval(recompute, PHASE_TICK_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, onActivity);
      window.clearInterval(ticker);
    };
  }, [
    enabled,
    hiddenLongAfterMs,
    hiddenThrottleAfterMs,
    idleAfterMs,
    resumeMinHiddenMs,
  ]);

  // 꺼져 있으면 원래(가장 빠른) 주기를 그대로 돌려준다 — 조절기가 기능을 바꾸지 않는다는 뜻.
  return enabled ? tiers[phase] : tiers.active;
}
